'use client'

import { useMediaQuery } from '@mantine/hooks'
import { useAtomValue, useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import { useEffect, useLayoutEffect, useRef } from 'react'

import { ROUTES } from '@/constants'
import { usePathname } from '@/i18n-routing'

import {
	altLandingQrTypeAtom,
	hasLandedFromAltLandingAtom,
	previewModeAtom,
	qrTypeAtom,
} from '../../generatorState'
import { QR_TYPE } from '../../generatorTypes'
import { QrTypeCard, TGeneratorType } from '../QrTypeCard/QrTypeCard'

export type TSelectQrTypeProps = {
	typeList: TGeneratorType[]
	rendersOn?: typeof ROUTES.altLanding | typeof ROUTES.generator.typeStep
	centeredCardLayout?: boolean
	cardClassName?: string
	onMounted?: () => void
}

export const SelectQrType = ({
	typeList,
	rendersOn = ROUTES.generator.typeStep,
	centeredCardLayout = false,
	cardClassName,
	onMounted,
}: TSelectQrTypeProps) => {
	const pathname = usePathname()
	const pathnameMatchesAltLanding = pathname === ROUTES.altLanding
	const pathnameMatchesTypeStep = pathname === ROUTES.generator.typeStep
	const userHasClicked = useRef(false)
	const qrType = useAtomValue(qrTypeAtom)
	const setQrType = useSetAtom(qrTypeAtom)
	const setAltLandingQrType = useSetAtom(altLandingQrTypeAtom)
	const setHasLandedFromAltLanding = useSetAtom(hasLandedFromAltLandingAtom)

	useLayoutEffect(() => {
		onMounted?.()
	}, [onMounted])

	useEffect(() => {
		setQrType(null)
		setAltLandingQrType(null)
	}, [setAltLandingQrType, setQrType])

	useHydrateAtoms([
		[previewModeAtom, 'preview'],
		[qrTypeAtom, qrType],
	])

	const hasHoverSupport = useMediaQuery('(hover: hover)')

	const hoverEndHandlerTimeoutIdRef = useRef<number | undefined>(undefined)

	const clearHoverTimeout = () => {
		clearTimeout(hoverEndHandlerTimeoutIdRef.current)
		userHasClicked.current = true
	}

	const handleHoverStart = (qrType: QR_TYPE) => {
		if (!hasHoverSupport) {
			return
		}
		clearTimeout(hoverEndHandlerTimeoutIdRef.current)
		if (
			rendersOn === ROUTES.generator.typeStep &&
			!pathnameMatchesAltLanding
		) {
			setQrType(qrType)
			setHasLandedFromAltLanding(false)
		}
		if (pathnameMatchesAltLanding) {
			setAltLandingQrType(qrType)
			setHasLandedFromAltLanding(true)
		}
	}

	const handleHoverEnd = () => {
		// If the user has clicked on a QR type card, we don't want to perform the hover end handler.
		if (!hasHoverSupport || userHasClicked.current) {
			return
		}
		hoverEndHandlerTimeoutIdRef.current = setTimeout(() => {
			const isStillOnTypePage =
				pathnameMatchesTypeStep || pathnameMatchesAltLanding
			if (isStillOnTypePage && !pathnameMatchesAltLanding) {
				setQrType(null)
			}
			if (isStillOnTypePage && pathnameMatchesAltLanding) {
				setAltLandingQrType(null)
			}
		}, 1000) as unknown as number
	}

	return typeList.map((type) => (
		<li key={type.name}>
			<QrTypeCard
				type={type}
				onHoverStart={handleHoverStart}
				onHoverEnd={handleHoverEnd}
				onClick={clearHoverTimeout}
				className={cardClassName}
				rendersOn={rendersOn}
				centeredLayout={centeredCardLayout}
			/>
		</li>
	))
}
